import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { config } from '../config';

const api = axios.create({
  baseURL: config.env.API_URL,
  headers: { 'Content-Type': 'application/json' },
});

let onLogout: (() => void) | null = null;
let isLoggingOut = false;

export function setAuthLogoutHandler(fn: () => void): void {
  onLogout = fn;
}

export function resetAuthLogoutGuard(): void {
  isLoggingOut = false;
}

function isAuthEndpoint(url = ''): boolean {
  return /^\/?auth(\/|$)/.test(url);
}

/* ── Keeping an active session alive ─────────────────────────────────────────
 *
 * The API's token lasts two hours from login and nothing used to renew it, so a user in
 * the middle of a long form — Add Project is seven steps — was logged out mid-task with
 * "Session has expired", however busy they were, and lost what they had typed.
 *
 * `GET /api/auth/session-status` hands back a fresh token, but only for one that is still
 * valid. So the token is renewed while it still is: once less than RENEW_WHEN_LEFT_MS is
 * left, the next request — or the next click or keystroke, see AuthProvider — renews it.
 * A session left idle past the two hours still ends, as it should.
 */
const RENEW_WHEN_LEFT_MS = 30 * 60 * 1000;
let renewing: Promise<void> | null = null;

function tokenExpiresAt(token: string): number | null {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    return exp ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Renews the stored token when it is close to expiring. Safe to call as often as wanted:
 * it does nothing until renewal is due, and concurrent callers share one request.
 */
export function renewTokenIfDue(): Promise<void> {
  const token = localStorage.getItem(config.AUTH_LS_KEYS.TOKEN);
  if (!token) return Promise.resolve();

  const expiresAt = tokenExpiresAt(token);
  if (!expiresAt) return Promise.resolve();

  const left = expiresAt - Date.now();
  // Already expired: nothing can renew it, and the request will say so itself.
  if (left <= 0 || left > RENEW_WHEN_LEFT_MS) return Promise.resolve();

  if (!renewing) {
    // A bare axios call, so it neither passes back through this interceptor nor trips the
    // logout-on-401 handler: a failed renewal just leaves the current token in place.
    renewing = axios
      .get(`${config.env.API_URL}/api/auth/session-status`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const fresh = res.data?.token;
        // Not if the user logged out or in again while this was in flight.
        if (typeof fresh === 'string' && fresh && localStorage.getItem(config.AUTH_LS_KEYS.TOKEN) === token) {
          localStorage.setItem(config.AUTH_LS_KEYS.TOKEN, fresh);
        }
      })
      .catch(() => {})
      .finally(() => {
        renewing = null;
      });
  }
  return renewing;
}

// Request interceptor
api.interceptors.request.use(async (request: InternalAxiosRequestConfig) => {
  if (!/(^|\/)auth\//.test(request.url ?? '')) await renewTokenIfDue();

  const token = localStorage.getItem(config.AUTH_LS_KEYS.TOKEN);

  if (token) {
    request.headers.Authorization = `Bearer ${token}`;
  }

  return request;
});

interface ApiResponseData {
  status?: boolean | string;
  message?: string;
  errors?: Record<string, string[]>;
  /** Per-field problems, e.g. `{ label: "already exists" }`. */
  messages?: Record<string, unknown>;
  error?: string;
}

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse<ApiResponseData>) => {
    const { data } = response;

    if (data?.status === false || data?.status === 'false') {
      const error: any = new Error(data.message || 'Operation failed');
      error.userMessage = data.message || 'Operation failed';
      error.response = response;
      return Promise.reject(error);
    }

    return response;
  },
  (error: any) => {
    const status = error.response?.status;
    const url = error.response?.config?.url;

    if (status === 401 && !isAuthEndpoint(url)) {
      if (!isLoggingOut) {
        isLoggingOut = true;
        onLogout?.();
      }
    }

    const apiError: ApiResponseData | undefined = error.response?.data;

    let message: string =
      apiError?.message ||
      apiError?.error ||
      error.response?.statusText ||
      error.message ||
      'Something went wrong';

    if (apiError?.errors && typeof apiError.errors === 'object') {
      message = Object.values(apiError.errors).flat().join(', ');
    }

    // The CRUD controllers report field problems under `messages` (UserController and the
    // lookup masters) rather than `errors`. Without this a 422 reads as "Validation Error",
    // which tells the user nothing about which field is wrong.
    if (!apiError?.message && apiError?.messages && typeof apiError.messages === 'object') {
      const fields = Object.entries(apiError.messages as Record<string, unknown>)
        .map(([field, problem]) => `${field} ${String(problem)}`)
        .join(', ');
      if (fields) message = fields;
    }

    error.userMessage = message;
    return Promise.reject(error);
  }
);

export default api;
